/** Date arithmetic for the session picker's calendar.
 *
 * ⚠️⚠️ NEVER `new Date("2026-08-28")`. That parses as UTC midnight, and
 * every reader east of Greenwich then gets the day BEFORE out of
 * `getDate()`. This app runs on IST and its archive is keyed by IST trade
 * date, so that bug would silently show the wrong weekday against every
 * session and mis-highlight the selected day. Sessions are therefore split
 * into numbers by hand and only ever handed to the `(y, m, d)` constructor,
 * which is local and cannot drift.
 *
 * The calendar is a VIEW over the archive, not a date picker. Only captured
 * sessions are selectable — the dropdown this replaces existed precisely
 * because indices joined the capture at different times, and a free picker
 * lets you ask for a NIFTY day that exists and a FINNIFTY day that does
 * not, with an unexplained empty chain as the only feedback.
 */

export interface YMD {
  y: number;
  m: number;
  d: number;
}

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** "2026-08-28" -> {y: 2026, m: 8, d: 28}. Month is 1-based, as written. */
export function parseSession(iso: string): YMD | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return null;
  return { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) };
}

export function toISO({ y, m, d }: YMD): string {
  return `${String(y)}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/** 0 = Sunday. Built from the local constructor, never from a parsed string. */
export function weekdayOf({ y, m, d }: YMD): number {
  return new Date(y, m - 1, d).getDay();
}

/** "Fri, 28 Aug 2026" — the weekday is the point. A trading session reads
 * differently depending on the day of the week (expiry days, Mondays after
 * a weekend gap), and a bare ISO date hides it. */
export function formatSession(iso: string): string {
  const ymd = parseSession(iso);
  if (!ymd) return iso;
  return `${DAY_NAMES[weekdayOf(ymd)] ?? ""}, ${String(ymd.d)} ${MONTH_NAMES[ymd.m - 1] ?? ""} ${String(ymd.y)}`;
}

export function monthLabel(y: number, m: number): string {
  return `${MONTH_NAMES[m - 1] ?? ""} ${String(y)}`;
}

export function daysInMonth(y: number, m: number): number {
  // Day 0 of the NEXT month is the last day of this one.
  return new Date(y, m, 0).getDate();
}

/** Six weeks of day numbers, `null` where the cell belongs to a
 * neighbouring month. Always 6x7 so the grid does not change height as
 * months are stepped through — a calendar that resizes under the cursor
 * moves the day you were reaching for. */
export function monthMatrix(y: number, m: number): (number | null)[][] {
  const lead = weekdayOf({ y, m, d: 1 });
  const total = daysInMonth(y, m);
  const cells: (number | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: total }, (_, i) => i + 1),
  ];
  while (cells.length < 42) cells.push(null);
  return Array.from({ length: 6 }, (_, r) => cells.slice(r * 7, r * 7 + 7));
}

/** Step a month, rolling the year. */
export function stepMonth(y: number, m: number, delta: number): { y: number; m: number } {
  const zero = y * 12 + (m - 1) + delta;
  return { y: Math.floor(zero / 12), m: (((zero % 12) + 12) % 12) + 1 };
}

/** True when the month holds at least one captured session — used to stop
 * the arrows wandering into empty years. */
export function monthHasSession(sessions: readonly string[], y: number, m: number): boolean {
  const prefix = `${String(y)}-${String(m).padStart(2, "0")}-`;
  return sessions.some((s) => s.startsWith(prefix));
}

/** The nearest month at or beyond `delta` that holds a session, or null if
 * there is none in that direction. Lets a single arrow press skip the
 * holiday months rather than dead-ending on them. */
export function nextMonthWithSession(
  sessions: readonly string[],
  y: number,
  m: number,
  delta: number,
): { y: number; m: number } | null {
  let cur = { y, m };
  // The archive spans months, not centuries; 240 steps is 20 years of head
  // room and a hard stop against an empty list looping forever.
  for (let i = 0; i < 240; i += 1) {
    cur = stepMonth(cur.y, cur.m, delta);
    if (monthHasSession(sessions, cur.y, cur.m)) return cur;
  }
  return null;
}

/** Which years the archive covers, ascending.
 *
 * The month arrows were enough while the archive was one month long. The
 * Breeze backfill (2026-09-04) took it to 167 sessions across nine months,
 * and reaching January from September became eight clicks — hence the
 * month and year grids, and hence these two helpers behind them.
 */
export function sessionYears(sessions: readonly string[]): number[] {
  const years = new Set<number>();
  for (const iso of sessions) {
    const ymd = parseSession(iso);
    if (ymd) years.add(ymd.y);
  }
  return [...years].sort((a, b) => a - b);
}

/** The months of `y` the archive holds, as 1-12.
 *
 * Used to grey out the rest: a month with no sessions is shown but dead,
 * the same rule the day grid follows, so the shape of the archive stays
 * visible instead of the gaps being hidden.
 */
export function sessionMonths(sessions: readonly string[], y: number): Set<number> {
  const months = new Set<number>();
  for (const iso of sessions) {
    const ymd = parseSession(iso);
    if (ymd?.y === y) months.add(ymd.m);
  }
  return months;
}
